import React, { useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Slide from '@mui/material/Slide';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ColorTextField from './UI/ColorTextField';
import Slider from '@mui/material/Slider';
import { DEFAULT_TRANSPARENCY_TOLERANCE } from '../constants/export_constants';
import { parseTransparencyColor } from '../image_utilities/TransparencyColor';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const SaveDialog = ({
  onTransparentColorChange = () => {},
  onNameChange = () => {},
  onEyeDropperClick = () => {},
  currentTransparentColor = '',
  transparencyTolerance = DEFAULT_TRANSPARENCY_TOLERANCE,
  onTransparencyToleranceChange = () => {},
  onClickSave = () => {},
  onClose = () => {},
  isOpen = false,
  isLoading = false,
  progress = 0,
  error = null,
  keepPreviousFrames = false,
  onKeepPreviousFramesChange = () => {},
  preview = null,
}) => {
  const [inputIsValid, setInputIsValid] = useState({ transparentColor: true });
  const canSubmit = Object.values(inputIsValid).every(Boolean);
  return (
    <Dialog
      onClose={onClose}
      open={isOpen}
      TransitionComponent={Transition}
    >
      <DialogTitle>Save Options</DialogTitle>
      <DialogContent>
        <ColorTextField
          onColorChange={(color, isValid) => {
            setInputIsValid({ ...inputIsValid, transparentColor: isValid });
            onTransparentColorChange(color, isValid);
          }}
          label="Transparent Color"
          value={currentTransparentColor !== '' ? currentTransparentColor : ''}
          onEyeDropperClick={e => {
            setInputIsValid({ ...inputIsValid, transparentColor: true });
            onEyeDropperClick(e);
          }}
          showEyeDropper={!isLoading}
          disabled={isLoading}
          allowEmpty
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Choose a background color to remove, or leave blank to keep existing transparency.
        </Typography>
        {parseTransparencyColor(currentTransparentColor) !== null && (
          <Box sx={{ mt: 2, px: 1 }}>
            <Typography id="transparency-tolerance-label">
              Color tolerance: {transparencyTolerance}%
            </Typography>
            <Slider
              aria-labelledby="transparency-tolerance-label"
              aria-describedby="transparency-tolerance-help"
              getAriaValueText={value => `${value}%`}
              value={transparencyTolerance}
              onChange={(_event, value) => onTransparencyToleranceChange(value)}
              min={0}
              max={100}
              step={1}
              disabled={isLoading}
            />
            <Typography id="transparency-tolerance-help" variant="body2" color="text.secondary">
              Increase to remove similar background shades and edge speckles.
              Lower it to preserve more detail. 0% removes only exact matches.
            </Typography>
          </Box>
        )}
        <Box display="flex" alignItems="flex-end">
          <TextField onChange={onNameChange} label="Image Name" disabled={isLoading} />
          <Typography>.gif</Typography>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={keepPreviousFrames}
              onChange={onKeepPreviousFramesChange}
              disabled={isLoading}
              slotProps={{ input: { 'aria-describedby': 'keep-frames-help' } }}
            />
          }
          label="Keep previous frames"
        />
        <Typography id="keep-frames-help" variant="body2" color="text.secondary">
          Leave earlier frames visible through transparent areas in the saved GIF.
        </Typography>
        {isOpen && !isLoading && canSubmit && preview}
        {error && <Typography role="alert" color="error">{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          variant="contained"
          onClick={onClickSave}
          disabled={isLoading || !canSubmit}
          className="glint-tactile"
        >
          Save
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={onClose}
          className="glint-tactile"
        >
          Cancel
        </Button>
      </DialogActions>
      {isLoading && <LinearProgress variant="determinate" value={progress} aria-label="Saving GIF" />}
    </Dialog>
  );
};

SaveDialog.propTypes = {
  onTransparentColorChange: PropTypes.func,
  onNameChange: PropTypes.func,
  onClickSave: PropTypes.func,
  onClose: PropTypes.func,
  onEyeDropperClick: PropTypes.func,
  currentTransparentColor: PropTypes.string,
  transparencyTolerance: PropTypes.number,
  onTransparencyToleranceChange: PropTypes.func,
  isOpen: PropTypes.bool,
  isLoading: PropTypes.bool,
  progress: PropTypes.number,
  error: PropTypes.string,
  keepPreviousFrames: PropTypes.bool,
  onKeepPreviousFramesChange: PropTypes.func,
  preview: PropTypes.node,
};

export default SaveDialog;
